import React, { useEffect, useState } from 'react'
import classNames from 'classnames'

import { CustomTooltip } from '@elo-kit/components/elo-ui/custom-tooltip/CustomTooltip'
import { InfoTooltip } from '@elo-kit/components/info-tooltip/InfoTooltip'
import { LoadingMask } from '@elo-kit/components/loading-mask/LoadingMask'
import { LoadingSpinner } from '@elo-kit/components/loading-spinner/LoadingSpinner'
import CommunityForm from 'shared/components/community/discussion/form/CommunityForm'
import PostsWidget from 'shared/components/community/components/PostsWidget'
import CommunityFilters from 'shared/components/community/filters/CommunityFilters'
import CreatePostForm from 'shared/components/community/components/create-post-form/CreatePostForm'
import MembersList from 'shared/components/community/members/MembersList'
import PayerCommunityForm from 'shared/components/community/components/payer-form/PayerCommunityForm'

import { withStores } from 'utils/helpers.utils'

import { POSITIONS } from '@elo-kit/constants/general.constants'

import './community-preview.scss'

const CommunityPreview = (props) => {
  const {
    communitiesStore,
    communityPostsStore,
    communityMemberStore,
    communityLabelsStore,
    productsStore,
    urlCommunityId,
    editMode,
    setEditMode,
    membersPreviewMode,
    setMembersPreviewMode,
    historyPush,
    payerCommunityId,
    isPayerCabinet,
  } = props
  const { item: currentMember } = communityMemberStore
  const { loading, handleGroupingChange, creatingPostLoading } = communityPostsStore
  const { item } = communitiesStore
  const { productId, name, membersCount, postsAllowed } = item || {}
  const { hash } = window.location

  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [backToMembers, setBackToMembers] = useState(false)

  const communityId = isPayerCabinet ? payerCommunityId : urlCommunityId

  const fetchCommunity = async () => {
    if (hash.includes('edit')) {
      setEditMode(true)
    }

    const resp = await communitiesStore.fetchItem(communityId)
    const { productId } = resp?.data || {}

    if (!isPayerCabinet) {
      await productsStore.fetchItem(productId)
    }

    await Promise.all([
      communityPostsStore.fetchList({ communityId }),
      communityMemberStore.fetchItem(communityId),
      communityLabelsStore.fetchList({ communityId }),
    ])
  }

  useEffect(() => {
    fetchCommunity()

    return () => {
      setMembersPreviewMode(false)
      setEditMode(false)
      communityPostsStore.clean()
      communitiesStore.clean()
    }
  }, [])

  const cogIconClasses = classNames('fas fa-cog', {
    active: editMode,
  })

  const membersIconClasses = classNames('fas fa-users', {
    active: membersPreviewMode,
  })

  const membersCountClasses = classNames('community-preview__header--members', {
    active: membersPreviewMode,
  })

  const previewHeaderClasses = classNames('community-preview__header', {
    'community-preview__header--no-margin': editMode,
  })

  const backToTheCommunity = () => {
    if (editMode && !isPayerCabinet) {
      setShowConfirmationModal(true)
    } else {
      setEditMode(false)
    }

    setMembersPreviewMode(false)
  }

  const openEditMode = () => {
    setEditMode(true)
    setMembersPreviewMode(false)

    if (!isPayerCabinet) {
      historyPush('#edit')
    }
  }

  const openMembersPreview = () => {
    if (editMode && !isPayerCabinet) {
      setShowConfirmationModal(true)
      setBackToMembers(true)
    } else {
      setEditMode(false)
      setMembersPreviewMode(true)
    }
  }

  const onConfirm = () => {
    if (backToMembers) {
      setMembersPreviewMode(true)
      setBackToMembers(false)
    }
  }

  const showCreatePostForm = !isPayerCabinet || (isPayerCabinet && postsAllowed && !currentMember.muted)

  return (
    <div className='community-preview'>
      <div className={previewHeaderClasses}>
        <div>
          {editMode || membersPreviewMode ? (
            <i className='fas fa-arrow-left' onClick={backToTheCommunity} />
          ) : (
            <div className='community-preview__header--name'>{name}</div>
          )}
        </div>
        <div className='community-preview__header--icons'>
          <CustomTooltip
            id='users'
            placement={POSITIONS.bottom}
            component
            tooltipIcon={(id) => (
              <>
                <i id={id} className={membersIconClasses} onClick={openMembersPreview} />
                <span className={membersCountClasses}>{membersCount}</span>
              </>
            )}
            body={I18n.t('react.cabinet.community.members')}
          />
          <i className={cogIconClasses} onClick={openEditMode} />
        </div>
      </div>
      {!editMode && !membersPreviewMode && (
        <>
          {creatingPostLoading ? (
            <LoadingSpinner className='loading-spinner__medium-container' />
          ) : (
            <>{showCreatePostForm && <CreatePostForm />}</>
          )}
          <div className='community-preview__filters'>
            <CommunityFilters
              filterLabels={communityLabelsStore.list}
              {...{
                handleGroupingChange,
                communityId,
                selectedUser,
                handleClear: () => {
                  setSelectedUser(null)
                  handleGroupingChange({ communityId })
                },
              }}
            />
          </div>
          {loading ? (
            <LoadingSpinner className='loading-spinner__medium-container' />
          ) : (
            <PostsWidget communityId={communityId} isPayerCabinet={isPayerCabinet} />
          )}
        </>
      )}

      {editMode && (
        <div className='community-preview__setting-form'>
          {isPayerCabinet ? (
            <>
              <div className='community-preview__setting-form--title'>
                {I18n.t('react.shared.comminity.profile_settings')}
              </div>
              <PayerCommunityForm
                toggle={() => setEditMode(!editMode)}
                submitButtonChild={I18n.t('react.shared.button.save')}
                communityId={communityId}
                isCommunitySettings
              />
            </>
          ) : (
            <>
              <div className='fields-container fields-container--left-align'>
                <div className='page-section__title'>{I18n.t('react.shared.settings')}</div>
                <InfoTooltip
                  id='community_settings_popover'
                  body={I18n.t('react.cabinet.help_icon.community_settings.content')}
                />
              </div>
              {communitiesStore.loading ? (
                <LoadingSpinner className='loading-spinner__medium-container' />
              ) : (
                <CommunityForm
                  productId={productId}
                  community={communitiesStore.item}
                  showConfirmationModal={showConfirmationModal}
                  setShowConfirmationModal={setShowConfirmationModal}
                  setEditMode={setEditMode}
                  onConfirm={onConfirm}
                  isCommunitySettings
                  historyPush={historyPush}
                />
              )}
            </>
          )}
        </div>
      )}
      {membersPreviewMode && (
        <MembersList
          communityId={communityId}
          handleFilterByUser={(user) => {
            setSelectedUser(user)
            handleGroupingChange({ memberId: user.id, communityId })
            backToTheCommunity()
          }}
          isPayerCabinet={isPayerCabinet}
        />
      )}

      {communitiesStore.loading && <LoadingMask />}
    </div>
  )
}

const stores = [
  'communitiesStore',
  'communityPostsStore',
  'communityMemberStore',
  'communityLabelsStore',
  'productsStore',
]
export default withStores(stores, CommunityPreview)
